import { useState, useLayoutEffect } from 'react';

export const useVisibleChildren = (
  containerRef: React.RefObject<HTMLElement | null>,
  wrapperRef: React.RefObject<HTMLElement | null>,
  overflowLabelRef: React.RefObject<HTMLElement | null>,
  totalItems: number
): { visibleCount: number; hiddenCount: number; hasMeasured: boolean } => {
  const [visibleCount, setVisibleCount] = useState(totalItems);
  const [hasMeasured, setHasMeasured] = useState(false);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const wrapper = wrapperRef.current;
    const overflowLabel = overflowLabelRef.current;
    if (!container || !wrapper || !overflowLabel) return;

    const recompute = (): void => {
      const children = Array.from(wrapper.children);
      const overflowLabelWidth = overflowLabel.offsetWidth;
      const containerWidth = container.clientWidth;
      let visible = 0;
      let totalWidth = 0;

      // Calculate how many children fit, accounting for the overflow label space
      for (const child of children) {
        const el = child as HTMLElement;
        const childWidth = el.offsetWidth;

        // Check if we can fit this child + the overflow label (if there are more items)
        const wouldNeedOverflowLabel = visible + 1 < totalItems;
        const requiredWidth =
          totalWidth +
          childWidth +
          (wouldNeedOverflowLabel ? overflowLabelWidth : 0);

        if (requiredWidth <= containerWidth) {
          visible++;
          totalWidth += childWidth;
        } else {
          break;
        }
      }

      setVisibleCount(visible);
      setHasMeasured(true);
    };

    recompute();

    const observer = new ResizeObserver(recompute);
    observer.observe(container);
    return () => observer.disconnect();
  }, [containerRef, wrapperRef, overflowLabelRef, totalItems]);

  const hiddenCount = totalItems - visibleCount;
  return { visibleCount, hiddenCount, hasMeasured };
};
